import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { FolderLine } from "@dropbox/dig-icons/assets";
import type { DocumentFollowUpSuggestion } from "@mirage/mosaics/QuerySuggestions/useDocumentFollowUpSuggestions";
import { PersonMetadata } from "@mirage/search/SearchResults/Metadata/PersonMetadata";
import { getPersonForMetadata } from "@mirage/search/SearchResults/util/resultUtil";
import type { SearchResult } from "@mirage/service-dbx-api";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { getConnectorIconFromResult } from "@mirage/shared/icons";
import { formatFileSize } from "@mirage/shared/util/tiny-utils";
import * as React from "react";
import { useTranslation } from "react-i18next";
import styles from "./DocumentDetailsPanel.module.css";
import { FollowUpChips } from "./FollowUpChips";

type DocumentDetailsPanelProps = {
  result: SearchResult;
  showMoreDetails: boolean;
  onToggleDetails: () => void;
  onSuggestionClick: (suggestion: DocumentFollowUpSuggestion) => void;
  isSummaryFirst?: boolean;
};

const MetadataEntry = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  return (
    <div className={styles.metadataEntry}>
      <div className={styles.metadataLabel}>
        <Text size="small" color="subtle">
          {label}
        </Text>
      </div>
      <div className={styles.metadataValue}>{value}</div>
    </div>
  );
};

export function DocumentDetailsPanel({
  result,
  showMoreDetails,
  onToggleDetails,
  onSuggestionClick,
  isSummaryFirst = false,
}: DocumentDetailsPanelProps) {
  const { t } = useTranslation();

  const isDarkMode = useTheme();
  const ConnectorIcon = React.useMemo(
    () => getConnectorIconFromResult(result, isDarkMode),
    [result, isDarkMode],
  );

  const { person } = getPersonForMetadata({
    sender: result.sender,
    creator: result.creator,
    lastModifier: result.lastModifier,
    filteredModifier: result.filteredModifier,
  });

  const updatedByValue = person ? (
    <Text size="small">
      <PersonMetadata person={person} />
    </Text>
  ) : (
    <Text size="small">{t("unknown")}</Text>
  );
  const fileSize = result.media?.size;

  const createdDate =
    result.providerCreatedAtMs || result.media?.datetimeOriginalMs;

  const renderFileSize = () => {
    if (!fileSize) return null;

    return (
      <MetadataEntry
        label={t("file_size_label")}
        value={<Text size="small">{formatFileSize(fileSize, t)}</Text>}
      />
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.detailsPanel}>
        <div className={styles.sectionHeader}>
          <Text size="medium" isBold className={styles.sectionTitle}>
            {t("details")}
          </Text>
        </div>

        <div className={styles.detailsContainer}>
          <MetadataEntry
            label={t("source_label")}
            value={
              <>
                <UIIcon
                  src={ConnectorIcon}
                  size="small"
                  className={styles.connectorIcon}
                />
                <Text size="small">{result.connectorInfo.displayName}</Text>
              </>
            }
          />

          <MetadataEntry
            label={t("saved_in_label")}
            value={
              <>
                <UIIcon src={FolderLine} size="small" />
                <Text size="small">
                  {result.parent?.displayName || t("placeholder_source")}
                </Text>
              </>
            }
          />

          <MetadataEntry label={t("updated_by_label")} value={updatedByValue} />

          <MetadataEntry
            label={t("last_updated_label")}
            value={
              <Text size="small">
                {result.providerUpdateAtMs
                  ? new Date(result.providerUpdateAtMs).toLocaleString()
                  : t("unknown")}
              </Text>
            }
          />

          {showMoreDetails && (
            <>
              {renderFileSize()}
              <MetadataEntry
                label={t("created_label")}
                value={
                  <Text size="small">
                    {createdDate
                      ? new Date(createdDate).toLocaleString()
                      : t("unknown")}
                  </Text>
                }
              />
            </>
          )}
        </div>

        <Button
          variant="transparent"
          size="small"
          onClick={onToggleDetails}
          className={styles.showMoreButton}
        >
          {showMoreDetails ? t("show_less_answer") : t("show_more")}
        </Button>
      </div>

      <FollowUpChips
        result={result}
        onSuggestionClick={onSuggestionClick}
        isSummaryFirst={isSummaryFirst}
      />
    </div>
  );
}
